import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const PolygonDrawingOverlay = ({
  sceneData,
  isDrawingMode,
  onPolygonComplete,
  onDrawModeChange,
  onDraftPointsChange,
  polygons,
  setPolygons,
  targetMeshRef,
  style,
}) => {
  const { camera, gl, scene } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const boxMeshRef = useRef(null);
  const [draftPoints, setDraftPoints] = useState(null);
  const [mousePos3D, setMousePos3D] = useState(null);
  const [blinkProgress, setBlinkProgress] = useState(0);
  const closePointDistance = 5;

  // Blink animation using useFrame
  useFrame((state, delta) => {
    setBlinkProgress((prev) => prev + delta * 1000);
  });

  // Find the active cube/box mesh
  useEffect(() => {
    const findBox = () => {
      scene.traverse((obj) => {
        if (obj.isMesh && obj.geometry?.type === 'BoxGeometry') {
          const params = obj.geometry?.parameters;
          if (params && (params.width > 100 || params.height > 100)) {
            boxMeshRef.current = obj;
          }
        }
      });
    };

    findBox();
    const timeout = setTimeout(findBox, 500);
    return () => clearTimeout(timeout);
  }, [scene]);

  // Handle mouse move for preview line
  const handlePointerMove = useCallback(
    (event) => {
      if (!isDrawingMode || !boxMeshRef.current || !draftPoints) return;

      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObject(boxMeshRef.current);

      if (intersects.length > 0) {
        const point = intersects[0].point.clone();
        if (intersects[0].object.parent) {
          intersects[0].object.parent.worldToLocal(point);
        }
        setMousePos3D(point);
      }
    },
    [isDrawingMode, draftPoints, camera, gl]
  );

  // Handle click on box
  const handlePointerDown = useCallback(
    (event) => {
      if (!isDrawingMode || !boxMeshRef.current) return;

      event.stopPropagation();

      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);
      const intersects = raycaster.current.intersectObject(boxMeshRef.current);

      if (intersects.length > 0) {
        const point = intersects[0].point.clone();

        if (draftPoints === null) {
          setDraftPoints([point]);
          setMousePos3D(point);
        } else {
          const firstPoint = draftPoints[0];
          const distance = point.distanceTo(firstPoint);

          if (distance < closePointDistance && draftPoints.length >= 3) {
            const newPolygon = {
              id: Date.now(),
              points: [...draftPoints],
            };
            setPolygons((prev) => [...prev, newPolygon]);
            onPolygonComplete?.(newPolygon);
            setDraftPoints(null);
            setMousePos3D(null);
            onDrawModeChange(false);
          } else {
            setDraftPoints((prev) => [...prev, point]);
          }
        }
      }
    },
    [
      isDrawingMode,
      draftPoints,
      camera,
      gl,
      setPolygons,
      onPolygonComplete,
      onDrawModeChange,
    ]
  );

  // Update draft points count
  useEffect(() => {
    onDraftPointsChange?.(draftPoints?.length || 0);
  }, [draftPoints, onDraftPointsChange]);

  // Handle keyboard
  useEffect(() => {
    if (!isDrawingMode) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setDraftPoints(null);
        setMousePos3D(null);
        onDrawModeChange(false);
      } else if (e.key === 'Enter' && draftPoints && draftPoints.length >= 3) {
        const newPolygon = {
          id: Date.now(),
          points: [...draftPoints],
        };
        setPolygons((prev) => [...prev, newPolygon]);
        onPolygonComplete?.(newPolygon);
        setDraftPoints(null);
        setMousePos3D(null);
        onDrawModeChange(false);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        if (draftPoints && draftPoints.length > 0) {
          setDraftPoints((prev) => {
            const newPoints = prev.slice(0, -1);
            if (newPoints.length > 0) {
              setMousePos3D(newPoints[newPoints.length - 1].clone());
            } else {
              setMousePos3D(null);
            }
            return newPoints;
          });
        } else {
          setDraftPoints(null);
          setMousePos3D(null);
          onDrawModeChange(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isDrawingMode,
    draftPoints,
    setPolygons,
    onPolygonComplete,
    onDrawModeChange,
  ]);

  // Reset when exiting draw mode
  useEffect(() => {
    if (!isDrawingMode) {
      setDraftPoints(null);
      setMousePos3D(null);
    }
  }, [isDrawingMode]);

  // Build line segments for draft polygon
  const draftSegments = useMemo(() => {
    if (!draftPoints || draftPoints.length < 2) return [];
    const segments = [];
    for (let i = 0; i < draftPoints.length - 1; i++) {
      segments.push([draftPoints[i], draftPoints[i + 1]]);
    }
    if (mousePos3D && draftPoints.length > 0) {
      segments.push([draftPoints[draftPoints.length - 1], mousePos3D]);
      if (draftPoints.length >= 3) {
        const dist = mousePos3D.distanceTo(draftPoints[0]);
        if (dist < closePointDistance) {
          segments.push([mousePos3D, draftPoints[0]]);
        }
      }
    }
    return segments;
  }, [draftPoints, mousePos3D]);

  // Build completed polygon shapes (for filling) - using THREE.ShapeUtils.triangulateShape
  const polygonFillGeometry = useMemo(() => {
    return polygons
      .map((poly) => {
        if (!poly.points || poly.points.length < 3) return null;

        const points = poly.points;

        // Calculate plane normal from first 3 points
        const v1 = new THREE.Vector3().subVectors(points[1], points[0]);
        const v2 = new THREE.Vector3().subVectors(points[2], points[0]);
        let normal = new THREE.Vector3().crossVectors(v1, v2);

        // Handle collinear points
        if (normal.length() < 0.0001) {
          const tempV = new THREE.Vector3(1, 0, 0);
          normal = new THREE.Vector3().crossVectors(v1, tempV);
          if (normal.length() < 0.0001) {
            normal = new THREE.Vector3(0, 0, 1);
          }
        }
        normal.normalize();

        // Determine projection plane based on dominant normal component
        let u, v;
        if (Math.abs(normal.x) > 0.9) {
          u = new THREE.Vector3(0, 1, 0);
          v = new THREE.Vector3(0, 0, 1);
        } else if (Math.abs(normal.y) > 0.9) {
          u = new THREE.Vector3(1, 0, 0);
          v = new THREE.Vector3(0, 0, 1);
        } else {
          u = new THREE.Vector3(1, 0, 0);
          v = new THREE.Vector3(0, 1, 0);
        }

        // Project 3D points to 2D
        const points2D = points.map(
          (p) => new THREE.Vector2(p.dot(u), p.dot(v))
        );

        // Use Three.js built-in triangulation
        let triangles;
        try {
          triangles = THREE.ShapeUtils.triangulateShape(points2D, []);
        } catch (e) {
          console.warn('Triangulation error for polygon:', poly.id, e);
          triangles = [];
        }
        if (triangles.length === 0) {
          console.warn(
            'No triangles for polygon:',
            poly.id,
            'pts:',
            points.length
          );
          return null;
        }

        // Build vertices from triangle indices
        const vertices = new Float32Array(triangles.length * 9);
        let idx = 0;
        for (const tri of triangles) {
          for (const i of tri) {
            vertices[idx++] = points[i].x;
            vertices[idx++] = points[i].y;
            vertices[idx++] = points[i].z;
          }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(vertices, 3)
        );

        return { id: poly.id, geometry, style: poly.style };
      })
      .filter(Boolean);
  }, [polygons]);

  // Render if: in drawing mode OR has polygons to display
  if (!isDrawingMode && polygons.length === 0) return null;

  return (
    <group>
      {/* Invisible click box - same size as cube */}
      {isDrawingMode && (
        <mesh
          ref={boxMeshRef}
          scale={[-1, 1, 1]}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
        >
          <boxGeometry args={[1000, 1000, 1000]} />
          <meshBasicMaterial visible={false} side={THREE.DoubleSide} />
        </mesh>
      )}

      {/* Draft polygon lines - BRIGHT YELLOW */}
      {draftSegments.map((seg, idx) => {
        const points = [seg[0], seg[1]];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        return (
          <line key={`draft-seg-${idx}`} geometry={geometry}>
            <lineBasicMaterial color="#ffff00" linewidth={4} />
          </line>
        );
      })}

      {/* Mouse preview point - PULSING YELLOW */}
      {mousePos3D && draftPoints && draftPoints.length > 0 && (
        <group position={mousePos3D}>
          <mesh>
            <sphereGeometry args={[4, 16, 16]} />
            <meshBasicMaterial color="#ffff00" transparent opacity={0.4} />
          </mesh>
          <mesh>
            <sphereGeometry args={[1.5, 16, 16]} />
            <meshBasicMaterial color="#ffff00" />
          </mesh>
        </group>
      )}

      {/* Completed polygons - Filled area with blinking */}
      {polygonFillGeometry.map((poly) => {
        const polyStyle = poly.style || {};
        const polyColor = polyStyle.color || '#f7dd36';
        const polyOpacity = polyStyle.opacity ?? 1;
        const polyBlinkEnabled = polyStyle.blinkEnabled ?? true;
        const polyBlinkSpeed = polyStyle.blinkSpeed ?? 2500;

        // Calculate dim opacity for blinking effect
        let finalOpacity = polyOpacity;
        if (polyBlinkEnabled) {
          const phase =
            (blinkProgress % polyBlinkSpeed) / polyBlinkSpeed;
          const dimFactor = 0.3 + 0.7 * Math.abs(Math.cos(phase * Math.PI));
          finalOpacity = polyOpacity * dimFactor;
        }

        return (
          <mesh
            key={`poly-fill-${poly.id}`}
            geometry={poly.geometry}
            renderOrder={1000}
          >
            <meshBasicMaterial
              color={polyColor}
              transparent={finalOpacity < 1}
              opacity={finalOpacity}
              side={THREE.DoubleSide}
              depthTest={false}
              polygonOffset={true}
              polygonOffsetFactor={1}
              polygonOffsetUnits={1}
            />
          </mesh>
        );
      })}
    </group>
  );
};

export default PolygonDrawingOverlay;
